import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserLayout from '../../components/user/UserLayout';
import { CreditCard } from 'lucide-react';
import { apiRequest } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function UserReviewStandees() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [standees, setStandees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchStandees();
  }, [user, navigate]);

  const fetchStandees = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await apiRequest(`${BACKEND_URL}/api/user/cards`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Filter for review standees only (those without type or not digital-card)
        const allCards = response.data.cards || [];
        const reviewStandees = allCards.filter(card => !card.order_data?.type || card.order_data?.type !== 'digital-card');
        setStandees(reviewStandees);
      } else {
        setError('Failed to fetch standees');
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading standees...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-navy">My Review Standees</h1>
          <p className="text-gray-600 mt-2">View and manage your delivered review standees</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {standees.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {standees.map((standee) => (
              <div key={standee.transaction_id} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
                {/* Card Header */}
                <div className="bg-gradient-to-br from-navy to-purple-600 p-6 text-white">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{standee.order_data?.displayName || 'Review Standee'}</h3>
                      <p className="text-sm opacity-90 mt-1">{standee.order_data?.cardType || 'Review Standee'}</p>
                    </div>
                    <CreditCard className="w-8 h-8 opacity-80" />
                  </div>
                  <div className="flex items-center gap-2 text-sm opacity-90">
                    <span className="px-2 py-1 bg-white/20 rounded">
                      {standee.order_data?.selectedMaterial?.name || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Order ID:</span>
                      <span className="font-mono font-semibold">#{standee.transaction_id?.slice(0, 10)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Delivered:</span>
                      <span className="font-semibold text-green-600">
                        {new Date(standee.updated_at || standee.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Standee Tracking Stats */}
                  {standee.dynamic_link_details && (
                    <div className="border-t border-gray-200 pt-4">
                      {/* Quick Stats */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-purple-600 font-medium">NFC Taps</p>
                          <p className="text-xl font-bold text-purple-800">
                            {standee.dynamic_link_details.nfc_scan_count || 0}
                          </p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-green-600 font-medium">QR Scans</p>
                          <p className="text-xl font-bold text-green-800">
                            {standee.dynamic_link_details.qr_scan_count || 0}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => navigate(`/user/scan-history/${standee.dynamic_link_details.code}`)}
                        className="w-full px-4 py-3 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors flex items-center justify-center gap-2"
                        data-testid={`view-scan-history-${standee.transaction_id}`}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        View Scan History
                      </button>
                    </div>
                  )}

                  {!standee.dynamic_link_details && (
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-xs text-gray-500 text-center">Standee will be activated once delivered</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-navy mb-2">No Standees Yet</h3>
            <p className="text-gray-600 mb-6">
              Your delivered standees will appear here. Start by placing an order!
            </p>
            <button
              onClick={() => navigate('/shop')}
              className="px-6 py-3 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors"
              data-testid="browse-standees-btn"
            >
              Browse Standees
            </button>
          </div>
        )}
      </div>
    </UserLayout>
  );
}
